import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';

/**
 * The frame of every screen of the template. Values follow the partner's checkout, where the client
 * lands next: the client should read the amount offer as coming from the same page.
 */
const Root = styled.div`
  --font-sans: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  --font-mono: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  --bg: #fafaf9;
  --surface: #fff;
  --surface-2: #f5f4f1;
  --line: #e8e6e1;
  --line-strong: #d8d4cc;
  --ink: #0a0a0a;
  --ink-muted: #6b6a66;
  --ink-faint: #9a9893;
  --primary: oklch(0.48 0.16 255);
  --primary-hover: oklch(0.42 0.16 255);
  --primary-soft: oklch(0.95 0.03 255);
  --danger: oklch(0.55 0.2 27);
  --danger-soft: oklch(0.96 0.03 27);
  --success: oklch(0.55 0.15 155);
  --success-soft: oklch(0.95 0.04 155);
  --error: oklch(0.56 0.18 25);
  --error-soft: oklch(0.96 0.04 25);
  --warn: oklch(0.62 0.14 75);
  --r-sm: 6px;
  --r-md: 10px;
  --r-lg: 14px;
  --shadow-card: 0 1px 2px rgba(20, 18, 15, 0.04), 0 8px 24px rgba(20, 18, 15, 0.06);

  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: var(--surface-2);
  color: var(--ink);
  font-family: var(--font-sans);
  font-feature-settings: 'ss01', 'tnum';
  -webkit-font-smoothing: antialiased;

  *, *::before, *::after {
    box-sizing: border-box;
  }

  @media (min-width: 560px) {
    padding: 32px 16px;
  }
`;

const Shell = styled.div<{ $fitContent?: boolean }>`
  background: var(--bg);
  width: 100%;
  max-width: 480px;
  min-height: 100vh;
  min-height: 100dvh;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  @media (min-width: 560px) {
    min-height: ${({ $fitContent }) => ($fitContent ? '0' : '640px')};
    border-radius: var(--r-lg);
    box-shadow: var(--shadow-card);
  }
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  padding-top: calc(14px + env(safe-area-inset-top, 0px));
  border-bottom: 1px solid var(--line);
  background: var(--surface);
  flex: 0 0 auto;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--ink);
`;

const LogoMark = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: var(--ink);
  color: var(--bg);
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: 700;
`;

const Secure = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--ink-muted);
  letter-spacing: -0.005em;

  svg {
    width: 12px;
    height: 12px;
  }
`;

const StageHost = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
`;

const Footer = styled.footer`
  flex: 0 0 auto;
  border-top: 1px solid var(--line);
  padding: 14px 20px;
  padding-bottom: calc(14px + env(safe-area-inset-bottom, 0px));
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: var(--surface);
`;

const Brands = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  opacity: 0.6;
`;

const BrandMini = styled.span<{ $color: string }>`
  width: 30px;
  height: 18px;
  border-radius: 3px;
  display: inline-block;
  background: var(--surface-2);
  border: 1px solid var(--line);
  font-size: 9px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  text-align: center;
  line-height: 16px;
`;

const ThreeDs = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: var(--ink-muted);
  font-weight: 500;
`;

const ThreeDsMark = styled.span`
  display: inline-grid;
  place-items: center;
  padding: 0 5px;
  height: 18px;
  border-radius: 4px;
  background: var(--surface-2);
  border: 1px solid var(--line);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
`;

const ShieldIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' aria-hidden='true'>
    <path d='M12 3l8 3v6c0 4.5-3.2 7.8-8 9-4.8-1.2-8-4.5-8-9V6l8-3z' />
  </svg>
);

/**
 * `overlay` lies over the whole frame, header and footer included. `fitContent` lets the card on a
 * desktop end where its content ends: a screen shorter than the card would otherwise leave a void.
 */
export const QuasiShell = ({
  children,
  overlay,
  fitContent,
}: {
  children: ReactNode;
  overlay?: ReactNode;
  fitContent?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <Root>
      <Shell $fitContent={fitContent}>
        <Header>
          <Logo>
            <LogoMark aria-hidden='true'>D</LogoMark>
            Dualipay
          </Logo>
          <Secure>
            <ShieldIcon />
            {t('quasi.securePayment')}
          </Secure>
        </Header>
        <StageHost>{children}</StageHost>
        <Footer>
          <Brands>
            <BrandMini $color='#1a1f71'>VISA</BrandMini>
            <BrandMini $color='#eb001b'>MC</BrandMini>
            <BrandMini $color='#0f754e'>МИР</BrandMini>
          </Brands>
          <ThreeDs>
            <ThreeDsMark>3DS</ThreeDsMark>
            Secure 2.0
          </ThreeDs>
        </Footer>
        {overlay}
      </Shell>
    </Root>
  );
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

/** Content of a working screen. */
export const QuasiBody = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  padding: 28px 20px 16px;
  gap: 22px;
  overflow: hidden;
  animation: ${fadeIn} 180ms ease-out;
`;

/** Content of a screen that only reports a state, centred. */
export const QuasiStage = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 20px;
  gap: 16px;
  animation: ${fadeIn} 180ms ease-out;
`;

export const QuasiStageTitle = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--ink);
  text-wrap: balance;
`;

export const QuasiStageText = styled.p`
  margin: 0;
  max-width: 300px;
  font-size: 14px;
  line-height: 1.45;
  color: var(--ink-muted);
  text-wrap: pretty;
`;

export const QuasiStageActions = styled.div`
  width: 100%;
  max-width: 300px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

type StatusTone = 'success' | 'error' | 'time';

const TONE_COLORS: Record<StatusTone, { background: string; color: string }> = {
  success: { background: 'var(--success-soft)', color: 'var(--success)' },
  error: { background: 'var(--error-soft)', color: 'var(--error)' },
  time: { background: 'var(--surface-2)', color: 'var(--ink-muted)' },
};

const IconCircle = styled.div<{ $tone: StatusTone }>`
  width: 76px;
  height: 76px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: ${({ $tone }) => TONE_COLORS[$tone].background};
  color: ${({ $tone }) => TONE_COLORS[$tone].color};

  svg {
    width: 36px;
    height: 36px;
  }
`;

export const QuasiStatusIcon = ({ tone }: { tone: StatusTone }) => (
  <IconCircle $tone={tone}>
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
      {tone === 'success' && <path d='M5 13l4 4L19 7' />}
      {tone === 'error' && <path d='M6 6l12 12M18 6L6 18' />}
      {tone === 'time' && (
        <>
          <circle cx='12' cy='12' r='9' />
          <path d='M12 7v5l3.5 2' />
        </>
      )}
    </svg>
  </IconCircle>
);

export const QuasiPrimaryButton = styled.button`
  width: 100%;
  min-height: 54px;
  padding: 0 14px;
  border: 0;
  border-radius: var(--r-md);
  background: var(--primary);
  color: #fff;
  font-family: var(--font-sans);
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: background 0.15s, transform 0.05s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:hover:not(:disabled) {
    background: var(--primary-hover);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:disabled {
    background: var(--surface-2);
    color: var(--ink-faint);
    cursor: not-allowed;
  }
`;

export const QuasiSecondaryButton = styled(QuasiPrimaryButton)`
  min-height: 46px;
  background: var(--surface);
  color: var(--ink);
  border: 1px solid var(--line-strong);
  font-weight: 500;

  &:hover:not(:disabled) {
    background: var(--surface-2);
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const QuasiSpinner = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 3px solid var(--line);
  border-top-color: var(--primary);
  animation: ${spin} 0.9s linear infinite;
`;

const backdropIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const modalIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to { opacity: 1; transform: none; }
`;

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(10, 10, 10, 0.44);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  animation: ${backdropIn} 140ms ease-out;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 340px;
  padding: 24px 22px 20px;
  display: flex;
  flex-direction: column;
  border-radius: var(--r-lg);
  background: var(--surface);
  box-shadow: var(--shadow-card);
  text-align: center;
  animation: ${modalIn} 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--ink);
`;

const ModalText = styled.p`
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 1.45;
  color: var(--ink-muted);
`;

const ModalActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 18px;
`;

const DangerButton = styled(QuasiPrimaryButton)`
  background: transparent;
  color: var(--danger);
  border: 1px solid var(--line-strong);

  &:hover:not(:disabled) {
    background: var(--danger-soft);
    border-color: var(--danger);
  }
`;

interface CancelDialogProps {
  title: string;
  text: string;
  continueLabel: string;
  cancelLabel: string;
  cancelling?: boolean;
  onContinue: () => void;
  onCancel: () => void;
}

export const QuasiCancelDialog = ({
  title,
  text,
  continueLabel,
  cancelLabel,
  cancelling,
  onContinue,
  onCancel,
}: CancelDialogProps) => (
  <Backdrop>
    <Modal role='dialog' aria-modal='true' aria-label={title}>
      <ModalTitle>{title}</ModalTitle>
      <ModalText>{text}</ModalText>
      <ModalActions>
        <QuasiPrimaryButton type='button' onClick={onContinue}>{continueLabel}</QuasiPrimaryButton>
        <DangerButton type='button' data-sentry-element='variant-refuse' disabled={cancelling} onClick={onCancel}>
          {cancelLabel}
        </DangerButton>
      </ModalActions>
    </Modal>
  </Backdrop>
);
